import { ContentType } from '@creditsights/api-ui-web';
import { ContentTypeTag, Divider } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, RefObject } from 'react';
import AnalystsWidget from '../../../components/widgets/Analysts';
import { AskAnalystModalSubject } from '../../../components/widgets/Analysts/AnalystsWidget';
import { Article } from '../../../models';
import { calculateReadTime, formatDateTime, formatReadTime, isReadTimeVisible } from '../Article.logic';
import ArticleActions from '../ArticleActions';
import ArticleReportSources from '../ArticleReportSources/ArticleReportSources';
import { HEADER_DOM_ID } from './ArticleHeader.constants';
import styles from './ArticleHeader.module.scss';

const cx = classNames.bind(styles);

interface ArticleHeaderProps {
  isTabletPortrait: boolean;
  article: Article;
  isMorningComment: boolean;
  isContentReadyForPrint: boolean;
  askAnalystModalSubject: AskAnalystModalSubject;
  isCompact?: boolean;
  isDesktop?: boolean;
  articlePrintRef?: RefObject<HTMLDivElement>;
  isAnalystWidgetLoading?: boolean;
  setIsArticleAnalystWidgetLoading?: (value: boolean) => void;
}

const renderReadTime = (formattedReadTime: string) => (
  <>
    <Divider vertical />
    <div className={cx('read-time')} data-testid="article-read-time">
      {formattedReadTime}
    </div>
  </>
);

const ArticleHeader = ({
  isTabletPortrait,
  article,
  isCompact,
  isDesktop,
  isMorningComment,
  isContentReadyForPrint,
  articlePrintRef,
  askAnalystModalSubject,
  isAnalystWidgetLoading,
  setIsArticleAnalystWidgetLoading,
}: ArticleHeaderProps): ReactElement => {
  const formattedPublishDate = formatDateTime(article);
  const formattedReadTime = formatReadTime(calculateReadTime(article.wordCount, article.imageCount));
  const showReadTime = !isMorningComment && isReadTimeVisible(article);
  const isCovenantsArticle = article.articleType === ContentType.Covenants;

  return (
    <div className={cx('body')} id={HEADER_DOM_ID}>
      <h1
        className={cx('title', { 'article-tab-title': isCompact })}
        data-testid="article-title"
        dangerouslySetInnerHTML={{ __html: article.content.title }}
      />
      {isDesktop && !isCompact ? (
        <div className={cx('second-lvl')}>
          <ContentTypeTag contentType={article.articleType} dataTestId="article-type" />
          <div className={cx('publish-date')} data-testid="article-publish-date">
            {formattedPublishDate}
          </div>
          {showReadTime && renderReadTime(formattedReadTime)}
          <ArticleReportSources className={cx('source-link')} article={article} />
          <Divider vertical />
          <ArticleActions
            articlePrintRef={articlePrintRef}
            article={article}
            isContentReadyForPrint={isContentReadyForPrint}
            isAnalystWidgetLoading={isAnalystWidgetLoading}
          />
        </div>
      ) : (
        <>
          <div
            className={cx('second-lvl', { 'article-tab-second-lvl': isCompact })}
            data-testid="article-second-header"
          >
            <ContentTypeTag contentType={article.articleType} dataTestId="article-type" />
            <Divider vertical />
            <div className={cx('publish-date')} data-testid="article-publish-date">
              {formattedPublishDate}
            </div>
            {showReadTime && renderReadTime(formattedReadTime)}
            <ArticleReportSources className={cx('source-link')} article={article} />
          </div>
          <div className={cx('third-lvl')}>
            {article.canAccess && !isEmpty(article.analysts) && !isCompact && isTabletPortrait && (
              <>
                <AnalystsWidget
                  analysts={isCovenantsArticle ? article.analysts.slice(0, 1) : article.analysts}
                  askAnalystModalSubject={askAnalystModalSubject}
                  setIsArticleAnalystWidgetLoading={setIsArticleAnalystWidgetLoading}
                />
                <Divider vertical />
              </>
            )}
            <ArticleActions
              articlePrintRef={articlePrintRef}
              article={article}
              isContentReadyForPrint={isContentReadyForPrint}
              isCompact={isCompact}
              isAnalystWidgetLoading={isAnalystWidgetLoading}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleHeader;
